import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../Services/api';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faBell, 
    faClipboardList, 
    faLeaf, 
    faEnvelope, 
    faExclamationTriangle, 
    faTimes, 
    faExternalLinkAlt, 
    faDollarSign,
    faClock
} from '@fortawesome/free-solid-svg-icons';

const NotificationContext = createContext();

const getNotificationMetadata = (type) => {
    switch (type) {
        case 'task':
        case 'assignment':
            return {
                icon: faClipboardList,
                bgColor: '#eff6ff',
                iconColor: '#1d4ed8',
                accentColor: '#3b82f6',
                label: 'Task'
            };
        case 'leave':
        case 'payroll':
            return {
                icon: faLeaf,
                bgColor: '#f0fdf4',
                iconColor: '#15803d',
                accentColor: '#22c55e',
                label: 'HR / Leaves'
            };
        case 'expense':
            return {
                icon: faDollarSign,
                bgColor: '#f0fdf4',
                iconColor: '#15803d',
                accentColor: '#22c55e',
                label: 'HR / Expense'
            };
        case 'attendance':
            return {
                icon: faClock,
                bgColor: '#fef3c7',
                iconColor: '#b45309',
                accentColor: '#f59e0b',
                label: 'Attendance'
            };
        case 'chat':
        case 'message':
            return {
                icon: faEnvelope,
                bgColor: '#f2f7e4',
                iconColor: '#799F0C',
                accentColor: '#799F0C',
                label: 'Message'
            };
        case 'alert':
        case 'system':
            return {
                icon: faExclamationTriangle,
                bgColor: '#fff7ed',
                iconColor: '#c2410c',
                accentColor: '#f97316',
                label: 'System'
            };
        default:
            return {
                icon: faBell,
                bgColor: '#f0f9ff',
                iconColor: '#0369a1',
                accentColor: '#0ea5e9',
                label: 'Alert'
            };
    }
};

const playChime = () => {
    try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (AudioContext) {
            const ctx = new AudioContext();
            const now = ctx.currentTime;
            
            // Higher pitch first tone (A5)
            const osc1 = ctx.createOscillator();
            const gain1 = ctx.createGain();
            osc1.type = 'sine';
            osc1.frequency.setValueAtTime(880, now);
            gain1.gain.setValueAtTime(0, now);
            gain1.gain.linearRampToValueAtTime(0.05, now + 0.05);
            gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
            osc1.connect(gain1);
            gain1.connect(ctx.destination);
            
            // Delayed harmonizing tone (E6)
            const osc2 = ctx.createOscillator();
            const gain2 = ctx.createGain();
            osc2.type = 'sine';
            osc2.frequency.setValueAtTime(1318.51, now + 0.08);
            gain2.gain.setValueAtTime(0, now);
            gain2.gain.setValueAtTime(0, now + 0.08);
            gain2.gain.linearRampToValueAtTime(0.03, now + 0.12);
            gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
            osc2.connect(gain2);
            gain2.connect(ctx.destination);
            
            osc1.start(now);
            osc1.stop(now + 0.5);
            osc2.start(now + 0.08);
            osc2.stop(now + 0.6);
        }
    } catch (e) {
        console.warn("Sound play block:", e);
    }
};

const showNotificationToast = (newNotif, navigate, markAsRead) => {
    playChime();
    const meta = getNotificationMetadata(newNotif.notification_type);

    toast.custom((t) => (
        <div
            className={`custom-notif-toast ${t.visible ? 'animate-enter' : 'animate-leave'}`}
            style={{
                background: '#ffffff',
                color: '#1e293b',
                width: '350px',
                padding: '16px',
                borderRadius: '16px',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                borderLeft: `5px solid ${meta.accentColor}`,
                border: '1px solid #f1f5f9',
                display: 'flex',
                gap: '14px',
                position: 'relative',
                pointerEvents: 'auto',
                fontFamily: 'inherit'
            }}
        >
            <div 
                style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: meta.bgColor,
                    color: meta.iconColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px',
                    flexShrink: 0
                }}
            >
                <FontAwesomeIcon icon={meta.icon} />
            </div>

            <div style={{ flex: 1, overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                    <span style={{ fontSize: '10px', textTransform: 'uppercase', fontWeight: '800', color: meta.iconColor, letterSpacing: '0.5px' }}>
                        {meta.label}
                    </span>
                </div>
                <div style={{ fontWeight: 700, fontSize: '14px', color: '#0f172a', marginBottom: '4px', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                    {newNotif.title}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b', lineHeight: '1.4', marginBottom: '10px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {newNotif.message}
                </div>
                
                {newNotif.link && (
                    <button
                        onClick={() => {
                            navigate(newNotif.link);
                            markAsRead(newNotif.id);
                            toast.dismiss(t.id);
                        }}
                        style={{
                            background: meta.iconColor,
                            color: '#ffffff',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '8px',
                            fontSize: '11px',
                            fontWeight: 700,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: `0 4px 6px -1px ${meta.accentColor}33`
                        }}
                    >
                        <span>View Details</span>
                        <FontAwesomeIcon icon={faExternalLinkAlt} style={{ fontSize: '9px' }} />
                    </button>
                )}
            </div>

            <button
                onClick={() => toast.dismiss(t.id)}
                style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#94a3b8',
                    cursor: 'pointer',
                    fontSize: '14px',
                    position: 'absolute',
                    top: '12px',
                    right: '12px'
                }}
            >
                <FontAwesomeIcon icon={faTimes} />
            </button>
        </div>
    ), {
        duration: 6000
    });
};

export const NotificationProvider = ({ children }) => {
    const { user, isAuthenticated } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const navigate = useNavigate();

    const markAsRead = useCallback(async (id) => {
        try {
            await api.patch(`users/notifications/${id}/`, { is_read: true });
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error("Error marking notification as read:", error);
        }
    }, []);

    const isInitialLoad = useRef(true);

    const fetchNotifications = useCallback(async () => {
        if (!isAuthenticated) return;
        try {
            const response = await api.get('users/notifications/');
            const freshData = response.data || [];
            
            setNotifications(prev => {
                const prevIds = new Set(prev.map(n => n.id));
                const newItems = freshData.filter(n => !prevIds.has(n.id) && !n.is_read);

                if (!isInitialLoad.current && newItems.length > 0) {
                    newItems.forEach(newNotif => {
                        showNotificationToast(newNotif, navigate, markAsRead);
                    });
                }
                isInitialLoad.current = false;
                return freshData;
            });
            setUnreadCount(freshData.filter(n => !n.is_read).length);
        } catch (error) {
            console.error("Error fetching notifications:", error);
        }
    }, [isAuthenticated, navigate, markAsRead]);

    useEffect(() => {
        if (!isAuthenticated) return;

        fetchNotifications();

        const token = localStorage.getItem("accessToken");
        const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
        const wsHost = window.location.hostname === "localhost" ? "localhost:8000" : window.location.host;
        const wsUrl = `${wsProtocol}//${wsHost}/ws/notifications/?token=${token}`;

        let socket = null;
        let reconnectTimer = null;
        let pollInterval = null;
        let reconnectDelay = 2000;
        let intentionallyClosed = false;

        const startPolling = () => {
            if (pollInterval) return;
            console.warn("WS unavailable – falling back to 10s polling");
            pollInterval = setInterval(() => fetchNotifications(), 10000);
        };

        const stopPolling = () => {
            if (pollInterval) {
                clearInterval(pollInterval);
                pollInterval = null;
            }
        };

        const connect = () => {
            if (intentionallyClosed) return;
            socket = new WebSocket(wsUrl);

            socket.onopen = () => {
                console.log("Notification WS connected");
                reconnectDelay = 2000;
                // Keep polling active as a fallback for non-Redis server setups
            };

            socket.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.type === "notification") {
                        const newNotif = data.notification;
                        setNotifications((prev) => [newNotif, ...prev]);
                        setUnreadCount((prev) => prev + 1);
                        showNotificationToast(newNotif, navigate, markAsRead);
                    }
                } catch (err) {
                    console.error("WS Message Error:", err);
                }
            };

            socket.onerror = () => {};

            socket.onclose = () => {
                if (intentionallyClosed) return;
                console.warn(`Notification WS closed. Reconnecting in ${reconnectDelay / 1000}s…`);
                startPolling();
                reconnectTimer = setTimeout(() => {
                    reconnectDelay = Math.min(reconnectDelay * 2, 30000);
                    connect();
                }, reconnectDelay);
            };
        };

        connect();
        startPolling();

        return () => {
            intentionallyClosed = true;
            if (reconnectTimer) clearTimeout(reconnectTimer);
            stopPolling();
            if (socket) socket.close();
        };
    }, [isAuthenticated, fetchNotifications, navigate, markAsRead]);

    const markAllAsRead = async () => {
        try {
            await api.post('users/notifications/read-all/');
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error("Error marking all notifications as read:", error);
        }
    };

    return (
        <NotificationContext.Provider value={{ 
            notifications, 
            unreadCount, 
            markAsRead, 
            markAllAsRead,
            fetchNotifications 
        }}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotifications = () => useContext(NotificationContext);
